import type { FC } from "react";
import { useEffect, useMemo } from "react";

import { isServerSide } from "@jti/fe-common";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { selectAccountAgeVerificationStatus } from "@jti/store/selectors/selectAccountAgeVerificationStatus";
import { selectAccountContact } from "@jti/store/selectors/selectAccountContact";
import { selectAgeVerificationLimitationStatus } from "@jti/store/selectors/selectAgeVerificationLimitationStatus";
import { fetchAccountContact } from "@jti/store/slices/LEGACY__checkoutSlice";
import { redirectToTrialCheckoutPage } from "@jti/store/utils";
import { useTranslations } from "@jti/ui";

import { PageBaseLayout } from "@checkout/layouts/PageBaseLayout";
import getCartItemsCount from "@checkout/utils/getCartItemsCount";

import { CartPageAside } from "./components/CartPageAside";
import { CartPageContent } from "./components/CartPageContent";
import { CartPageHeader } from "./components/CartPageHeader";

type CartPageProps = {
  onNavigate?: (url: string) => void;
};

export const CartPage: FC<CartPageProps> = ({ onNavigate }) => {
  const { __ } = useTranslations();
  const dispatch = useDispatch();

  const isAgeVerificationErrorVisible = useSelector((state) => {
    return (
      selectAgeVerificationLimitationStatus(state) &&
      !selectAccountAgeVerificationStatus(state) &&
      Boolean(selectAccountContact(state))
    );
  });

  const updateAgeVerificationPending = useSelector((state) => state.checkout.updateAgeVerificationPending);

  const maxOrderLimit = useSelector((state) => state.cart.storeConfig?.order_limit_value);
  const grandTotalPrice = useSelector((state) => state.cart.prices.data?.grandTotal.value) ?? 0;
  const shippingPrice = useSelector((state) => state.cart.prices.data?.shipping?.value) ?? 0;
  const isLimitEnabled = useSelector((state) => state.cart.storeConfig?.is_order_limit_enabled);
  const items = useSelector((state) => state.cart.items);
  const freeTrialID = useSelector((store) => store.freeTrialCart.id);

  const itemsCount = getCartItemsCount(items);
  const isCartItemsLimitReached = isLimitEnabled && grandTotalPrice - shippingPrice > maxOrderLimit;
  const subtitle = useMemo(() => {
    if (itemsCount === 0) {
      return "";
    } else if (itemsCount === 1) {
      return __("cart.items_count.single");
    }

    return __("cart.items_count", [itemsCount]);
  }, [itemsCount]);

  useEffect(() => {
    dispatch(fetchAccountContact());
  }, []);

  useEffect(() => {
    if (!updateAgeVerificationPending) {
      dispatch(fetchAccountContact());
    }
  }, [dispatch, updateAgeVerificationPending]);

  useEffect(() => {
    if (!freeTrialID || isServerSide()) {
      return;
    }

    const urlSplit = window.location.href.split("cart.html#");

    if (urlSplit.length < 2 || urlSplit[1].length < 2) {
      redirectToTrialCheckoutPage();
    }
  }, [freeTrialID]);

  return (
    <PageBaseLayout
      header={<CartPageHeader subtitle={subtitle} />}
      content={
        <CartPageContent
          isAgeVerificationErrorVisible={isAgeVerificationErrorVisible}
          isCartItemsLimitReached={isCartItemsLimitReached}
        />
      }
      aside={
        <CartPageAside
          isAgeVerificationErrorVisible={isAgeVerificationErrorVisible}
          isCartItemsLimitReached={isCartItemsLimitReached}
          onNavigate={onNavigate}
        />
      }
      data-testid="regular-cart-page"
    />
  );
};
